import React, {useState, useCallback, useMemo} from 'react';
import {View, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, ScrollView, Platform, Modal} from 'react-native';
import {Text, Divider, Snackbar, IconButton, Chip, Dialog, Portal, Button, TextInput} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {paymentApi} from '../../services/api/paymentApi';
import {workerApi} from '../../services/api/workerApi';

const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE'];
const MODE_LABELS = {CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank Transfer', CHEQUE: 'Cheque'};

const SOURCE_OPTIONS = [
  {value: 'SIMPLE', label: 'Simple'},
  {value: 'BULK_PAYMENT', label: 'Bulk'},
];

const getMonthStart = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDateISO = date => {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/* ── Payout card (unchanged) ─────────────────────────────── */

const PayoutCard = ({item}) => {
  const [expanded, setExpanded] = useState(false);
  const items = item.items || [];
  const hasItems = items.length > 0;

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'});
  };

  const formatMode = mode => MODE_LABELS[mode] || mode;

  return (
    <View style={styles.card}>
      <View style={styles.cardRow}>
        <Text style={styles.txnCode}>{item.transactionCode}</Text>
        <Text style={styles.amount}>{'\u20B9'}{item.totalAmount}</Text>
      </View>
      <View style={styles.cardRow}>
        <Text style={styles.workerName}>{item.payeeName}</Text>
        <Text style={styles.date}>{formatDate(item.paymentDate)}</Text>
      </View>
      <View style={styles.cardRow}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
          <Text style={styles.modeBadge}>{formatMode(item.paymentMode)}</Text>
          {item.sourceType === 'BULK_PAYMENT' && (
            <Text style={styles.bulkBadge}>Bulk</Text>
          )}
        </View>
        {hasItems ? (
          <TouchableOpacity
            style={styles.itemCountBtn}
            onPress={() => setExpanded(prev => !prev)}
            activeOpacity={0.7}>
            <Text style={styles.itemCountText}>
              {item.itemCount} item{item.itemCount !== 1 ? 's' : ''}
            </Text>
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={colors.primary}
            />
          </TouchableOpacity>
        ) : (
          <Text style={styles.itemCount}>{item.itemCount} item{item.itemCount !== 1 ? 's' : ''}</Text>
        )}
      </View>
      {expanded && hasItems && (
        <View style={styles.expandedSection}>
          <View style={styles.expandedHeader}>
            <Text style={[styles.expandedHeaderText, {flex: 0.5}]}>Type</Text>
            <Text style={[styles.expandedHeaderText, {flex: 2}]}>Description</Text>
            <Text style={[styles.expandedHeaderText, {flex: 1.5}]}>Order#</Text>
            <Text style={[styles.expandedHeaderText, {flex: 1, textAlign: 'right'}]}>Amount</Text>
          </View>
          {items.map((pi, idx) => {
            const isTask = pi.type === 'Task';
            return (
              <View key={pi.id || idx} style={[styles.expandedRow, idx % 2 === 0 && styles.expandedRowAlt]}>
                <View style={{flex: 0.5}}>
                  <View style={[styles.typeBadge, {backgroundColor: isTask ? colors.primary + '25' : '#FF980025'}]}>
                    <Text style={[styles.typeBadgeText, {color: isTask ? colors.primary : '#FF9800'}]}>
                      {isTask ? 'T' : 'D'}
                    </Text>
                  </View>
                </View>
                <Text style={[styles.expandedCell, {flex: 2}]} numberOfLines={1}>
                  {pi.taskTypeName || (isTask ? 'Task' : 'Dress Assignment')}
                </Text>
                <Text style={[styles.expandedCell, {flex: 1.5}]} numberOfLines={1}>
                  {pi.orderNumber || '-'}
                </Text>
                <Text style={[styles.expandedCell, {flex: 1, textAlign: 'right', fontWeight: '600'}]}>
                  {'\u20B9'}{pi.amount}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

/* ── Main screen ─────────────────────────────────────────── */

const WorkerPayoutsScreen = () => {
  const {t} = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  // Filter state — default: this month
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterWorkerId, setFilterWorkerId] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(getMonthStart);
  const [filterEndDate, setFilterEndDate] = useState(getToday);
  const [filterSourceType, setFilterSourceType] = useState(null);
  const [filterPaymentMode, setFilterPaymentMode] = useState(null);
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');

  // Temp filter state
  const [tmpWorkerId, setTmpWorkerId] = useState(null);
  const [tmpStartDate, setTmpStartDate] = useState(getMonthStart);
  const [tmpEndDate, setTmpEndDate] = useState(getToday);
  const [tmpSourceType, setTmpSourceType] = useState(null);
  const [tmpPaymentMode, setTmpPaymentMode] = useState(null);
  const [tmpMinAmount, setTmpMinAmount] = useState('');
  const [tmpMaxAmount, setTmpMaxAmount] = useState('');

  // Date picker
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [datePickerTarget, setDatePickerTarget] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState(new Date());

  const hasFilters = filterWorkerId || filterStartDate || filterEndDate
    || filterSourceType || filterPaymentMode || filterMinAmount || filterMaxAmount;

  const workerMap = useMemo(() => {
    const map = {};
    workers.forEach(w => { map[w.id] = w.fullName; });
    return map;
  }, [workers]);

  const fetchPayouts = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const params = {};
      if (filterStartDate) params.from = formatDateISO(filterStartDate);
      if (filterEndDate) params.to = formatDateISO(filterEndDate);
      if (filterWorkerId) params.workerId = filterWorkerId;
      if (filterPaymentMode) params.paymentMode = filterPaymentMode;
      if (filterSourceType) params.sourceType = filterSourceType;
      if (filterMinAmount) params.minAmount = filterMinAmount;
      if (filterMaxAmount) params.maxAmount = filterMaxAmount;
      const res = await paymentApi.getHistory(params);
      setPayouts(res.data?.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to load payouts';
      setSnackbar({visible: true, message: msg});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterWorkerId, filterStartDate, filterEndDate, filterSourceType, filterPaymentMode, filterMinAmount, filterMaxAmount]);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await workerApi.search({page: 0, size: 200, isActive: true});
      setWorkers(res.data?.data?.content || []);
    } catch (e) { /* silent */ }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchPayouts();
      fetchWorkers();
    }, [fetchPayouts, fetchWorkers]),
  );

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPayouts(false);
  };

  const openFilterModal = useCallback(() => {
    setTmpWorkerId(filterWorkerId);
    setTmpStartDate(filterStartDate);
    setTmpEndDate(filterEndDate);
    setTmpSourceType(filterSourceType);
    setTmpPaymentMode(filterPaymentMode);
    setTmpMinAmount(filterMinAmount);
    setTmpMaxAmount(filterMaxAmount);
    setFilterVisible(true);
  }, [filterWorkerId, filterStartDate, filterEndDate, filterSourceType, filterPaymentMode, filterMinAmount, filterMaxAmount]);

  const applyFilters = useCallback(() => {
    setFilterWorkerId(tmpWorkerId);
    setFilterStartDate(tmpStartDate);
    setFilterEndDate(tmpEndDate);
    setFilterSourceType(tmpSourceType);
    setFilterPaymentMode(tmpPaymentMode);
    setFilterMinAmount(tmpMinAmount);
    setFilterMaxAmount(tmpMaxAmount);
    setFilterVisible(false);
  }, [tmpWorkerId, tmpStartDate, tmpEndDate, tmpSourceType, tmpPaymentMode, tmpMinAmount, tmpMaxAmount]);

  const clearFilters = useCallback(() => {
    setFilterWorkerId(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterSourceType(null);
    setFilterPaymentMode(null);
    setFilterMinAmount('');
    setFilterMaxAmount('');
    setTmpWorkerId(null);
    setTmpStartDate(null);
    setTmpEndDate(null);
    setTmpSourceType(null);
    setTmpPaymentMode(null);
    setTmpMinAmount('');
    setTmpMaxAmount('');
    setFilterVisible(false);
  }, []);

  // Date picker helpers
  const openDatePicker = useCallback((target) => {
    const currentVal = target === 'start' ? tmpStartDate : tmpEndDate;
    setDatePickerTarget(target);
    setDatePickerValue(currentVal || new Date());
    setFilterVisible(false);
    setTimeout(() => setDatePickerVisible(true), 300);
  }, [tmpStartDate, tmpEndDate]);

  const handleDatePickerConfirm = useCallback(() => {
    if (datePickerTarget === 'start') setTmpStartDate(datePickerValue);
    else if (datePickerTarget === 'end') setTmpEndDate(datePickerValue);
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget, datePickerValue]);

  const handleDatePickerCancel = useCallback(() => {
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, []);

  const handleAndroidDateChange = useCallback((event, date) => {
    if (event.type === 'dismissed') {
      setDatePickerVisible(false);
      setTimeout(() => setFilterVisible(true), 300);
      return;
    }
    if (date) {
      if (datePickerTarget === 'start') setTmpStartDate(date);
      else if (datePickerTarget === 'end') setTmpEndDate(date);
    }
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget]);

  const formatDateShort = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const formatDateDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {year: 'numeric', month: 'short', day: 'numeric'});
  };

  if (loading && payouts.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Filter chips bar */}
      <View style={styles.chipBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipScroll}>
          {filterWorkerId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterWorkerId(null)}>
              {workerMap[filterWorkerId] || 'Worker'}
            </Chip>
          )}
          {filterStartDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStartDate(null)}>
              From: {formatDateShort(filterStartDate)}
            </Chip>
          )}
          {filterEndDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterEndDate(null)}>
              To: {formatDateShort(filterEndDate)}
            </Chip>
          )}
          {filterSourceType && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterSourceType(null)}>
              {filterSourceType === 'BULK_PAYMENT' ? 'Bulk' : 'Simple'}
            </Chip>
          )}
          {filterPaymentMode && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterPaymentMode(null)}>
              {MODE_LABELS[filterPaymentMode]}
            </Chip>
          )}
          {filterMinAmount ? (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterMinAmount('')}>
              Min: {'\u20B9'}{filterMinAmount}
            </Chip>
          ) : null}
          {filterMaxAmount ? (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterMaxAmount('')}>
              Max: {'\u20B9'}{filterMaxAmount}
            </Chip>
          ) : null}
          {hasFilters && (
            <TouchableOpacity onPress={clearFilters} style={styles.clearBtn}>
              <Text style={styles.clearText}>Clear</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
        <IconButton
          icon={hasFilters ? 'filter-check' : 'filter-variant'}
          size={22}
          iconColor={hasFilters ? colors.primary : colors.textSecondary}
          onPress={openFilterModal}
          style={styles.filterIconBtn}
        />
      </View>

      <Divider />

      <FlatList
        data={payouts}
        keyExtractor={item => String(item.id)}
        renderItem={({item}) => <PayoutCard item={item} />}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        contentContainerStyle={payouts.length === 0 ? styles.emptyListContent : undefined}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>{t('worker.noPayouts')}</Text>
          </View>
        }
      />

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>

      {/* Filter Modal */}
      <Portal>
        <Dialog visible={filterVisible} onDismiss={() => setFilterVisible(false)} style={{borderRadius: 15}}>
          <View style={styles.dialogTitleRow}>
            <Text variant="titleLarge" style={{fontWeight: '600'}}>Filter Payouts</Text>
            <IconButton icon="close" size={22} onPress={() => setFilterVisible(false)} style={{margin: 0}} />
          </View>
          <Dialog.ScrollArea style={{maxHeight: 460, paddingHorizontal: 0}}>
            <ScrollView>
              <View style={styles.filterSection}>
                {/* Worker */}
                <Text variant="labelLarge" style={styles.filterLabel}>Worker</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {workers.map(w => {
                      const selected = tmpWorkerId === w.id;
                      return (
                        <TouchableOpacity
                          key={w.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setTmpWorkerId(selected ? null : w.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {w.fullName}
                          </Text>
                          {selected && <IconButton icon="check" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                {/* Date range */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Date</Text>
                <View style={styles.dateRow}>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('start')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpStartDate ? styles.dateText : styles.datePlaceholder}>
                      {tmpStartDate ? formatDateDisplay(tmpStartDate) : 'Start date'}
                    </Text>
                    {tmpStartDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpStartDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                  <Text style={styles.dateSep}>to</Text>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('end')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpEndDate ? styles.dateText : styles.datePlaceholder}>
                      {tmpEndDate ? formatDateDisplay(tmpEndDate) : 'End date'}
                    </Text>
                    {tmpEndDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpEndDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                </View>

                {/* Source type */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Type</Text>
                <View style={styles.filterChipGroup}>
                  {SOURCE_OPTIONS.map(s => (
                    <Chip key={s.value} selected={tmpSourceType === s.value}
                      onPress={() => setTmpSourceType(tmpSourceType === s.value ? null : s.value)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {s.label}
                    </Chip>
                  ))}
                </View>

                {/* Payment mode */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Mode</Text>
                <View style={styles.filterChipGroup}>
                  {PAYMENT_MODES.map(m => (
                    <Chip key={m} selected={tmpPaymentMode === m}
                      onPress={() => setTmpPaymentMode(tmpPaymentMode === m ? null : m)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {MODE_LABELS[m]}
                    </Chip>
                  ))}
                </View>

                {/* Amount range */}
                <Text variant="labelLarge" style={styles.filterLabel}>Amount Range</Text>
                <View style={styles.amountRow}>
                  <TextInput
                    mode="outlined"
                    placeholder="Min"
                    value={tmpMinAmount}
                    onChangeText={setTmpMinAmount}
                    keyboardType="numeric"
                    dense
                    style={styles.amountInput}
                    left={<TextInput.Affix text={'\u20B9'} />}
                  />
                  <Text style={styles.dateSep}>to</Text>
                  <TextInput
                    mode="outlined"
                    placeholder="Max"
                    value={tmpMaxAmount}
                    onChangeText={setTmpMaxAmount}
                    keyboardType="numeric"
                    dense
                    style={styles.amountInput}
                    left={<TextInput.Affix text={'\u20B9'} />}
                  />
                </View>
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={clearFilters}>Clear Filters</Button>
            <Button mode="contained" onPress={applyFilters}
              style={{borderRadius: 10}} buttonColor={colors.primary}>
              Apply Filters
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Date picker */}
      {Platform.OS === 'ios' ? (
        <Modal visible={datePickerVisible} transparent animationType="slide">
          <View style={styles.datePickerOverlay}>
            <View style={styles.datePickerSheet}>
              <View style={styles.datePickerHeader}>
                <Button onPress={handleDatePickerCancel}>Cancel</Button>
                <Text variant="titleMedium" style={{fontWeight: '600'}}>
                  {datePickerTarget === 'start' ? 'Start Date' : 'End Date'}
                </Text>
                <Button onPress={handleDatePickerConfirm}>Done</Button>
              </View>
              <DateTimePicker
                value={datePickerValue}
                mode="date"
                display="spinner"
                onChange={(event, date) => { if (date) setDatePickerValue(date); }}
                style={{height: 200}}
              />
            </View>
          </View>
        </Modal>
      ) : (
        datePickerVisible && (
          <DateTimePicker
            value={datePickerValue}
            mode="date"
            display="default"
            onChange={handleAndroidDateChange}
          />
        )
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  centerContent: {justifyContent: 'center', alignItems: 'center'},
  emptyListContent: {flex: 1},
  // Chip bar
  chipBar: {
    flexDirection: 'row', alignItems: 'center',
    paddingLeft: spacing.sm, backgroundColor: colors.surface,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  chipScroll: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.xs, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.primary + '20', height: 30},
  filterChipText: {fontSize: 12},
  clearBtn: {justifyContent: 'center', paddingHorizontal: spacing.xs},
  clearText: {color: colors.primary, fontSize: 12, fontWeight: '600'},
  filterIconBtn: {margin: 0},
  // Payout card
  card: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  txnCode: {fontSize: 14, fontWeight: '700', color: colors.text},
  amount: {fontSize: 15, fontWeight: '700', color: colors.primary},
  workerName: {fontSize: 13, color: colors.text},
  date: {fontSize: 13, color: colors.textSecondary},
  modeBadge: {
    fontSize: 12,
    color: colors.textSecondary,
    backgroundColor: colors.background,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  bulkBadge: {
    fontSize: 11,
    color: '#FF9800',
    backgroundColor: '#FF980015',
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 4,
    fontWeight: '600',
    overflow: 'hidden',
  },
  itemCount: {fontSize: 12, color: colors.textSecondary},
  itemCountBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 4,
    gap: 2,
  },
  itemCountText: {fontSize: 12, color: colors.primary, fontWeight: '600'},
  expandedSection: {
    marginTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: spacing.sm,
  },
  expandedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  expandedHeaderText: {fontSize: 11, fontWeight: '700', color: colors.textSecondary},
  expandedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
  },
  expandedRowAlt: {backgroundColor: '#FAFAFA'},
  expandedCell: {fontSize: 12, color: colors.text},
  typeBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadgeText: {fontSize: 10, fontWeight: '700'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 4},
  emptyText: {fontSize: 16, color: colors.textSecondary},
  // Filter modal styles
  dialogTitleRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.xs,
  },
  filterSection: {paddingHorizontal: spacing.lg, paddingVertical: spacing.sm},
  filterLabel: {fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.xs},
  filterChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  filterSelectChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  filterListBox: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, maxHeight: 100, overflow: 'hidden',
  },
  filterListItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5, paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  filterListItemSelected: {backgroundColor: colors.primary + '12'},
  filterListItemText: {fontSize: 14, color: colors.text},
  filterListItemTextSelected: {fontWeight: '600', color: colors.primary},
  dateRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  dateInput: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4,
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, paddingHorizontal: 6, paddingVertical: 8,
  },
  dateText: {fontSize: 12, color: colors.text, flex: 1},
  datePlaceholder: {fontSize: 12, color: colors.textSecondary, flex: 1},
  dateSep: {fontSize: 13, color: colors.textSecondary},
  amountRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  amountInput: {flex: 1, backgroundColor: colors.surface, fontSize: 14},
  // Date picker modal (iOS)
  datePickerOverlay: {
    flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.4)',
  },
  datePickerSheet: {
    backgroundColor: colors.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16,
    paddingBottom: 30,
  },
  datePickerHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
});

export default WorkerPayoutsScreen;
